'use client';
import Link from 'next/link';
import Navbar from '@/components/ui/Navbar';
import Footer from '@/components/ui/Footer';

const CONTACT_EMAIL = 'info@reprintsolution.online';
const LAST_UPDATED = '1 June 2026';

export default function RefundReturnPage() {
  return (
    <div className="min-h-screen bg-[#030812] text-white">
      <Navbar />
      <main>
        <section className="border-b border-white/10 bg-[linear-gradient(135deg,#07111f_0%,#0f1f3d_100%)] py-16">
          <div className="container mx-auto max-w-4xl px-4">
            <nav className="mb-6 flex items-center gap-2 text-sm text-slate-400">
              <Link href="/" className="hover:text-white transition">Home</Link>
              <span>/</span>
              <span className="text-white">Refund &amp; Return Policy</span>
            </nav>
            <h1 className="text-3xl font-black text-white md:text-4xl">Refund &amp; Return Policy</h1>
            <p className="mt-3 text-slate-400 text-sm">Last updated: {LAST_UPDATED}</p>
          </div>
        </section>

        <section className="py-14">
          <div className="container mx-auto max-w-4xl px-4">
            <div className="space-y-8 text-sm leading-7 text-slate-300">

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">Overview</h2>
                <p className="mt-3">
                  At RePrint Solution, we want you to be completely satisfied with your purchase. If you are not satisfied for any reason, we are here to help. Please read our refund and return policy carefully before making a purchase.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">Return Eligibility</h2>
                <p className="mt-3">To be eligible for a return, your item must meet the following conditions:</p>
                <ul className="mt-3 space-y-2 list-disc list-inside">
                  <li>The return request must be made within <strong className="text-white">14 days</strong> of receiving your order</li>
                  <li>The item must be in the same condition as received — unused and in its original packaging</li>
                  <li>You must have proof of purchase (order confirmation or receipt)</li>
                  <li>The item must not have been damaged due to misuse, accident, or unauthorised modification</li>
                </ul>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">Non-Returnable Items</h2>
                <p className="mt-3">The following items cannot be returned:</p>
                <ul className="mt-3 space-y-2 list-disc list-inside">
                  <li>Items that have been used, installed, or set up</li>
                  <li>Items damaged by the customer after delivery</li>
                  <li>Items returned more than 14 days after delivery</li>
                  <li>Consumables such as ink cartridges or toner that have been opened</li>
                </ul>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">How to Initiate a Return</h2>
                <p className="mt-3">To initiate a return, please follow these steps:</p>
                <ol className="mt-3 space-y-2 list-decimal list-inside">
                  <li>Contact us at <a href={`mailto:${CONTACT_EMAIL}`} className="text-violet-400 hover:text-violet-300 underline">{CONTACT_EMAIL}</a> with your order number and reason for return</li>
                  <li>We will review your request and respond within 2 business days</li>
                  <li>If approved, we will provide return shipping instructions</li>
                  <li>Pack the item securely in its original packaging</li>
                  <li>Ship the item to the address provided in our return authorisation email</li>
                </ol>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">Refunds</h2>
                <p className="mt-3">
                  Once we receive and inspect your returned item, we will notify you of the approval or rejection of your refund. If approved, your refund will be processed within <strong className="text-white">5–10 business days</strong> to your original payment method.
                </p>
                <p className="mt-3">
                  Please note that original shipping costs are non-refundable unless the return is due to our error (e.g., wrong item sent, item arrived damaged).
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">Faulty or Damaged Items</h2>
                <p className="mt-3">
                  If you receive a faulty or damaged item, please contact us within <strong className="text-white">48 hours</strong> of delivery with photos of the damage. We will arrange a replacement or full refund at no additional cost to you.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">Return Shipping Costs</h2>
                <p className="mt-3">
                  Unless the return is due to our error, the customer is responsible for return shipping costs. We recommend using a tracked shipping service as we cannot be held responsible for items lost in transit.
                </p>
              </div>

              <div className="rounded-xl border border-white/10 bg-white/[0.04] p-6">
                <h2 className="text-lg font-black text-white">Contact Us</h2>
                <p className="mt-3">
                  For any questions about our refund and return policy, please contact us at:{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-violet-400 hover:text-violet-300 underline">{CONTACT_EMAIL}</a>
                </p>
              </div>

            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
